import React, { memo, useState, useEffect, useCallback, type ReactElement } from 'react';
import { getJwmCreateProjectDrawerOptions } from '@atlassian/jira-business-common/src/common/utils/create-jwm-project-drawer/index.tsx';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { JiraPopup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { LOCATION } from '../../../../common/constants/index.tsx';
import { useProjectTemplates } from '../../../../services/project-templates/index.tsx';
import type {
	ProjectTemplatesDataType,
	OpenDrawerMethod,
} from '../../../../services/project-templates/types.tsx';
import CreateProjectButton from '../create-project-button/index.tsx';

type Props = {
	renderContent: (data: ProjectTemplatesDataType, loading: boolean) => ReactElement;
	openProjectCreateDrawer: OpenDrawerMethod;
};

const OneClickProjectButton = ({ renderContent, openProjectCreateDrawer }: Props) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { data, loading, error, fetch: fetchJwmTemplates } = useProjectTemplates();
	const { isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();

	const { templates, recommendationSessionId } = data;

	const wasFetched = templates != null || loading || error != null;

	const onClose = useCallback(() => setIsOpen(false), []);

	useEffect(() => {
		if (isOpen && !wasFetched) {
			fetchJwmTemplates();
		}
	}, [isOpen, wasFetched, fetchJwmTemplates]);

	useEffect(() => {
		if (isLeftSidebarCollapsed && isOpen) {
			onClose();
		}
	}, [isLeftSidebarCollapsed, isOpen, onClose]);

	useEffect(() => {
		if (error && isOpen) {
			openProjectCreateDrawer(getJwmCreateProjectDrawerOptions(LOCATION, recommendationSessionId));
			setIsOpen(false);
		}
	}, [error, isOpen, openProjectCreateDrawer, recommendationSessionId]);

	const content = useCallback(() => renderContent(data, loading), [renderContent, data, loading]);

	const trigger = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'triggerProps' implicitly has an 'any' type.
		(triggerProps) => (
			<div {...triggerProps}>
				<CreateProjectButton onClick={() => setIsOpen(!isOpen)} />
			</div>
		),
		[isOpen],
	);

	return (
		<JiraPopup
			messageId="navigation-apps-sidebar-core.ui.multi-project-menu.projects-header.one-click-project.jira-popup"
			messageType="transactional"
			isOpen={isOpen}
			placement="right-start"
			onClose={onClose}
			content={content}
			trigger={trigger}
		/>
	);
};

export default memo<Props>(OneClickProjectButton);
